import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Cpu,
  MousePointer2,
  Activity,
  Shield,
  Sprout,
  Beer,
  UtensilsCrossed,
  Eye,
  MessageSquare,
  Workflow,
  Smartphone,
  Lock,
  TrendingUp,
  Clock,
  AlertTriangle,
  BarChart3,
  Users,
  Layers,
  Twitter,
  Linkedin,
  ArrowRight,
  Wheat,
  Fence,
  Flag,
  Grape,
  Warehouse,
  LucideIcon,
  // Sensor icons
  Waves,
  Droplets,
  CloudSun,
  Gauge,
  Camera,
  Thermometer,
  Wind,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useResponsiveInfo } from "@/hooks/useResponsiveViewport";
import { PilotContactModal, ThankYouModal } from "@/components/pilot";
import { DemoModal } from "@/components/demo";
import { LandingFooter } from "@/components/layout/LandingFooter";
import { PublicNavigation } from "@/components/layout/PublicNavigation";
import { SEO, organizationStructuredData } from "@/components/seo/SEO";
import { trackEvent } from "@/services/tracking/googleAnalytics";

// ============================================================================
// DESIGN TOKENS (Style Guide Colors)
// ============================================================================
const colors = {
  primary: "hsl(var(--primary))",
  primaryHover: "hsl(var(--primary) / 0.9)",
  secondary: "#B2AC88", // Sage Green
  seafoam: "#93E9BE",
  blueGrotto: "#116B98",
  desertSun: "#E27D3D",
  charcoal: "#0A0A0A",
  midGray: "#6E6E6E",
  lightGray: "#F4F5F4",
  champagne: "#F7E7CE",
  white: "#FFFFFF",
};

// ============================================================================
// ANIMATION VARIANTS (respects prefers-reduced-motion)
// ============================================================================
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

// Reduced motion variant - no movement, just fade
const fadeInUpReduced = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

// Hook to detect reduced motion preference
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

// ============================================================================
// DATA
// ============================================================================
const whyItems = [
  {
    icon: Cpu,
    title: "See the Full Picture, Finally",
    description:
      "All your sensors, devices, and data streams in one place. Walk into work knowing exactly what's happening across every location. No more switching between dashboards or piecing together reports.",
  },
  {
    icon: MousePointer2,
    title: "Build What You Need, No Code Required",
    description:
      "Ask questions in plain English, get instant answers. Create the exact dashboards your business needs in minutes. You're in control of your data, not waiting on IT.",
  },
  {
    icon: Activity,
    title: "Stay Informed, Even When You're Away",
    description:
      "Get notified when something needs attention, check in from anywhere, and trust that your operation is running smoothly. Peace of mind comes standard.",
  },
  {
    icon: Shield,
    title: "Grow at Your Own Pace",
    description:
      "Add new sensors, locations, or team members whenever you're ready. Your system scales with your ambition, without rebuilding or starting over.",
  },
];

const howItWorksSteps = [
  {
    step: 1,
    icon: Layers,
    title: "Install Your Sensors",
    description: "From soil moisture to pressure gauges, get the sensors you need installed and transmitting in hours.",
    details: {
      headline: "Simple installation, powerful insights",
      paragraphs: [
        "Whether you need soil moisture sensors, flow meters, depth gauges, pressure monitors, or temperature probes, installation is straightforward. Mount them where you need data, and they start transmitting right away.",
        "No complex wiring or IT infrastructure required. Our sensors connect wirelessly and work out of the box. Install them yourself or have a professional handle it. Either way, you'll see live data the same day.",
        "Start small and expand on your own schedule. Add more sensors as your needs grow or budget allows. No pressure to buy everything upfront.",
      ],
      highlights: ["Wireless sensors", "Same-day data", "DIY or pro install"],
    },
  },
  {
    step: 2,
    icon: Workflow,
    title: "Add Automation",
    description: "Connect controllers to manage pumps, valves, lighting, and more from anywhere.",
    details: {
      headline: "Control your equipment remotely",
      paragraphs: [
        "Go beyond monitoring. Add smart controllers to automate pumps, irrigation valves, grow lights, fans, and other equipment. Turn things on or off from your phone, or let the system handle it automatically.",
        "Set rules based on your sensor data. When soil moisture drops, start the irrigation. When temperature rises, turn on the fans. Your equipment responds to real conditions, not guesswork.",
      ],
      highlights: ["Remote control", "Smart automation", "Works with sensors"],
    },
  },
  {
    step: 3,
    icon: Eye,
    title: "See Everything in One Place",
    description: "All your data flows into a single, clear dashboard. Ask questions in plain English and get instant answers.",
    details: {
      headline: "Finally, clarity across your entire operation",
      paragraphs: [
        "Every sensor, every location, every metric in one view. Ask questions in plain English like 'How much water did we use this month?' or 'Which fields need watering?' and get instant answers powered by AI.",
        "Share views with your team, set up screens for the shop floor, or check in from your phone. The data you need is always one question away.",
      ],
      highlights: ["AI-powered insights", "Natural language queries", "Access from any device"],
    },
  },
  {
    step: 4,
    icon: Shield,
    title: "Relax, It's Handled",
    description: "Your operation runs itself. Take a vacation, focus on growth, or just sleep better at night.",
    details: {
      headline: "Peace of mind, even when you're away",
      paragraphs: [
        "Once everything is set up, your farm or business runs smoothly without constant supervision. Go on vacation knowing you'll only hear from the system if something actually needs your attention.",
        "Spend your time on what matters: growing your business, being with family, or simply taking a well-deserved break. TerraMatrix keeps watch 24/7 so you don't have to.",
      ],
      highlights: ["24/7 monitoring", "Alerts only when needed", "Peace of mind"],
    },
  },
];

const industries = [
  {
    icon: Sprout,
    title: "Agriculture",
    subtitle: "Precision Farming",
    description:
      "Optimize crop production with AI-guided automation. Integrate soil moisture sensors and smart irrigation to deliver the right amount of water exactly when needed.",
    color: colors.primary,
    colorLight: "hsl(var(--primary) / 0.08)",
  },
  {
    icon: Beer,
    title: "Brewing",
    subtitle: "Smart Brewery Operations",
    description:
      "Maintain consistency and quality in every batch. Monitor fermentation tanks with precision sensors and automate cooling for optimal results.",
    color: colors.blueGrotto,
    colorLight: `${colors.blueGrotto}15`,
  },
  {
    icon: UtensilsCrossed,
    title: "Restaurants",
    subtitle: "Intelligent Kitchens",
    description:
      "Improve food safety, efficiency, and guest experience. Monitor equipment health and use AI-driven analytics for operational insights.",
    color: colors.desertSun,
    colorLight: `${colors.desertSun}15`,
  },
];

const featuredIndustries: { icon: LucideIcon; name: string }[] = [
  { icon: Wheat, name: "Farms" },
  { icon: Fence, name: "Ranches" },
  { icon: Beer, name: "Breweries" },
  { icon: Grape, name: "Wineries" },
  { icon: UtensilsCrossed, name: "Restaurants" },
  { icon: Flag, name: "Golf Courses" },
  { icon: Warehouse, name: "Greenhouses" },
  { icon: Sprout, name: "Nurseries" },
];

const sensorTypes: { icon: LucideIcon; name: string; description: string }[] = [
  { icon: Waves, name: "Depth Sensors", description: "Monitor water levels in tanks, reservoirs, and wells" },
  { icon: Droplets, name: "Soil Moisture", description: "Track soil conditions for optimal irrigation" },
  { icon: CloudSun, name: "Weather Stations", description: "Local weather data for informed decisions" },
  { icon: Gauge, name: "Pressure Sensors", description: "Monitor system pressure in pipes and tanks" },
  { icon: Camera, name: "Computer Vision", description: "AI-powered visual monitoring and analytics" },
  { icon: Thermometer, name: "Temperature", description: "Precise temperature monitoring anywhere" },
  { icon: Wind, name: "Flow Meters", description: "Measure liquid and gas flow rates" },
  { icon: Zap, name: "Energy Monitors", description: "Track power consumption and efficiency" },
];

const worries = [
  "Did I leave the irrigation running?",
  "Is the tank temperature dropping?",
  "What's happening at the farm right now?",
  "Will I wake up to a disaster?",
  "Are the pumps still working?",
  "Is anyone checking the sensors?",
  "Should I drive back to check?",
  "What if something fails overnight?",
];

const features = [
  {
    icon: Eye,
    title: "Real-Time Monitoring & Control",
    description:
      "Observe live data streams from all your sensors on a unified dashboard. Send control commands remotely to any connected device with immediate feedback.",
  },
  {
    icon: MessageSquare,
    title: "Natural Language Dashboards",
    description:
      'Create dashboards by simply asking. Use everyday language like "Show today\'s humidity vs. temperature" and TerraMatrix generates the visualization instantly.',
  },
  {
    icon: Workflow,
    title: "Automated Workflows & Alerts",
    description:
      "Configure powerful if/then rules and AI-driven automations. Automatically trigger actions based on sensor data and get notified at the right time.",
  },
  {
    icon: Smartphone,
    title: "Multi-Device Access",
    description:
      "Stay connected from anywhere. Access dashboards on desktop, tablet, or mobile. Field technicians and managers stay in sync wherever they are.",
  },
  {
    icon: Lock,
    title: "Modular, Scalable & Secure",
    description:
      "Hardware-agnostic platform that grows with you. End-to-end encryption and role-based permissions ensure your operations are protected.",
  },
];

const benefits = [
  {
    icon: TrendingUp,
    title: "Higher Efficiency & Quality",
    description: "Maintain optimal conditions automatically for consistent results.",
  },
  {
    icon: Clock,
    title: "Reduced Manual Work",
    description: "Automate routine monitoring so your team can focus on high-value work.",
  },
  {
    icon: Shield,
    title: "24/7 Peace of Mind",
    description: "Constant oversight with immediate alerts, even when you're off-site.",
  },
  {
    icon: AlertTriangle,
    title: "Prevented Downtime",
    description: "Catch problems early before they halt operations or spoil product.",
  },
  {
    icon: BarChart3,
    title: "Data-Driven Decisions",
    description: "Historical and real-time data at your fingertips for continuous improvement.",
  },
  {
    icon: Users,
    title: "Accountability & Collaboration",
    description: "Every action logged with clear audit trails. Shared dashboards keep teams aligned.",
  },
  {
    icon: Layers,
    title: "Future-Proof Scalability",
    description: "Add new sensors, equipment, or facilities without retooling your setup.",
  },
];

// ============================================================================
// PILOT PROGRAM TOP BANNER
// ============================================================================
interface PilotBannerProps {
  onApplyClick: () => void;
}

const PilotBanner: React.FC<PilotBannerProps> = ({ onApplyClick }) => {
  return (
    <div
      className="w-full py-2.5 px-4 text-center text-sm font-medium"
      style={{ backgroundColor: colors.primary }}
    >
      <span className="text-white/90">
        Now accepting applications for our Pilot Program
      </span>
      <button
        onClick={onApplyClick}
        className="ml-3 text-white underline underline-offset-2 hover:text-white/80 transition-colors font-semibold"
      >
        Apply Now →
      </button>
    </div>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================
interface HeroSectionProps {
  onContactClick: () => void;
  onDemoClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onContactClick, onDemoClick }) => {
  const navigate = useNavigate();

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video - Desktop */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-label="Background video showing TerraMatrix IoT platform capabilities"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover hidden lg:block"
      >
        <source src="/videos/HeroVideoDesktop.mp4" type="video/mp4" />
      </video>

      {/* Background Video - Tablet */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-label="Background video showing TerraMatrix IoT platform capabilities"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover hidden md:block lg:hidden"
      >
        <source src="/videos/HeroTablet.mp4" type="video/mp4" />
      </video>

      {/* Background Video - Mobile */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-label="Background video showing TerraMatrix IoT platform capabilities"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover block md:hidden"
      >
        <source src="/videos/HeroMobile.mp4" type="video/mp4" />
      </video>

      {/* Fallback gradient background if video doesn't load */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
        style={{ zIndex: -1 }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
          >
            The Operating System for{" "}
            <span className="italic">Real-World Infrastructure</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Modern tools for real-world operations.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              size="lg"
              className="text-base font-semibold min-w-[180px] px-8 py-6 rounded-full bg-background hover:bg-accent text-primary transition-all duration-200"
              onClick={onContactClick}
            >
              Get in Touch
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-base font-semibold min-w-[180px] px-8 py-6 rounded-full border border-white/40 bg-transparent text-white/80 hover:border-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
              onClick={onDemoClick}
            >
              Watch Demo
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 bg-white rounded-full mt-2"
          />
        </div>
      </motion.div>
    </section>
  );
};

// ============================================================================
// WHY TERRAMATRIX SECTION
// ============================================================================
const WhySection: React.FC = () => {
  return (
    <section className="pt-16 pb-12 md:pt-24 md:pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Run Your Operations with Confidence
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            When everything connects, you can focus on what matters most.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {whyItems.map((item, index) => (
            <motion.div
              key={item.title}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="group p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-200 hover:bg-white hover:shadow-lg hover:shadow-gray-200/50 transition-all duration-300"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300"
                style={{ backgroundColor: "hsl(var(--primary) / 0.07)" }}
              >
                <item.icon className="h-6 w-6" style={{ color: colors.primary }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.charcoal }}>
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: colors.midGray }}>
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// HOW IT WORKS SECTION - Sticky Scroll Layout with Flowing Design
// ============================================================================
const HowItWorksSection: React.FC = () => {
  const [activeStep, setActiveStep] = React.useState(0);
  const stepRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  React.useEffect(() => {
    const observers: IntersectionObserver[] = [];

    stepRefs.current.forEach((ref, index) => {
      if (ref) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setActiveStep(index);
              }
            });
          },
          { threshold: 0.5, rootMargin: "-30% 0px -20% 0px" }
        );
        observer.observe(ref);
        observers.push(observer);
      }
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  return (
    <section id="how-it-works" className="pt-12 pb-20 md:pt-16 md:pb-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Sticky on desktop */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Getting Started Is Simpler Than You Think
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            Install sensors yourself or work with a professional. Either way, you'll be up and running in no time.
          </motion.p>
        </motion.div>

        {/* Sticky scroll layout */}
        <div className="relative lg:grid lg:grid-cols-12 lg:gap-12">
          {/* Left column - Sticky step indicators (desktop) */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-[20vh]">
              <div className="relative">
                {/* Progress line - behind everything, stops at last step */}
                <div
                  className="absolute w-0.5 bg-gray-200 -z-10"
                  style={{ left: '19px', top: '20px', height: 'calc(100% - 110px)' }}
                >
                  <motion.div
                    className="w-full"
                    style={{
                      backgroundColor: colors.primary,
                      height: `${((activeStep + 1) / howItWorksSteps.length) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                {/* Step items */}
                <div className="relative z-10 space-y-2">
                  {howItWorksSteps.map((step, index) => {
                    const isActive = activeStep === index;
                    const StepIcon = step.icon;
                    return (
                      <motion.div
                        key={step.step}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-4 cursor-pointer"
                        onClick={() => {
                          stepRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                      >
                        {/* Step number circle */}
                        <div
                          className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 text-white`}
                          style={{ backgroundColor: isActive ? colors.primary : "#9CAF88" }}
                        >
                          {step.step}
                        </div>

                        {/* Step info - no card styling, just clean layout */}
                        <div
                          className={`flex-1 min-w-0 py-2 transition-all duration-300`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <StepIcon
                              className="h-4 w-4 transition-colors duration-300"
                              style={{ color: isActive ? colors.primary : colors.midGray }}
                            />
                            <h4
                              className={`font-semibold transition-colors duration-300 ${
                                isActive ? "" : "text-gray-500"
                              }`}
                              style={{ color: isActive ? colors.charcoal : undefined }}
                            >
                              {step.title}
                            </h4>
                          </div>
                          <p
                            className={`text-sm leading-relaxed transition-colors duration-300 ${
                              isActive ? "" : "text-gray-400"
                            }`}
                            style={{ color: isActive ? colors.midGray : undefined }}
                          >
                            {step.description}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Scrolling detail panels */}
          <div className="lg:col-span-8 space-y-8 lg:space-y-32">
            {howItWorksSteps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <motion.div
                  key={step.step}
                  ref={(el) => (stepRefs.current[index] = el)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                  className="scroll-mt-44 relative"
                >
                  {/* Mobile step indicator */}
                  <div className="flex items-center gap-4 mb-4 lg:hidden">
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white shadow-md"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {step.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold" style={{ color: colors.charcoal }}>
                        {step.title}
                      </h3>
                      <p className="text-sm" style={{ color: colors.midGray }}>
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Detail content - flowing design */}
                  <div className="relative pl-6 lg:pl-8 border-l-2" style={{ borderColor: "hsl(var(--primary) / 0.08)" }}>
                    {/* Icon */}
                    <div className="mb-5">
                      <div
                        className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                        style={{ backgroundColor: "hsl(var(--primary) / 0.03)" }}
                      >
                        <StepIcon className="h-7 w-7" style={{ color: colors.primary }} />
                      </div>
                    </div>

                    {/* Step label */}
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="text-sm font-semibold uppercase tracking-wider"
                        style={{ color: colors.primary }}
                      >
                        Step {step.step}
                      </span>
                      <div
                        className="h-px w-12"
                        style={{ backgroundColor: "hsl(var(--primary) / 0.15)" }}
                      />
                    </div>

                    {/* Headline */}
                    <h3
                      className="text-2xl lg:text-3xl font-bold mb-5"
                      style={{ color: colors.charcoal }}
                    >
                      {step.details.headline}
                    </h3>

                    {/* Paragraphs */}
                    <div className="space-y-4 mb-6">
                      {step.details.paragraphs.map((paragraph, pIndex) => (
                        <p
                          key={pIndex}
                          className="text-base lg:text-lg leading-relaxed"
                          style={{ color: colors.midGray }}
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2">
                      {step.details.highlights.map((highlight, hIndex) => (
                        <span
                          key={hIndex}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: "hsl(var(--primary) / 0.03)",
                            color: colors.primary,
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: colors.primary }}
                          />
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FEATURED INDUSTRIES SECTION - Marquee + Sensor Cards Grid
// ============================================================================
const FeaturedIndustriesSection: React.FC = () => {
  // Duplicate the array for seamless looping
  const duplicatedIndustries = [...featuredIndustries, ...featuredIndustries];

  return (
    <section className="py-12 md:py-16 overflow-hidden">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-sm font-semibold uppercase tracking-wider mb-8 text-center"
        style={{ color: colors.midGray }}
      >
        Built for operations like yours
      </motion.p>

      {/* Marquee container */}
      <div className="relative mb-16">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, white, transparent)' }} />
        <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, white, transparent)' }} />

        {/* Scrolling content */}
        <div className="flex animate-marquee hover:[animation-play-state:paused]">
          {duplicatedIndustries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <div
                key={`${industry.name}-${index}`}
                className="flex items-center gap-2.5 px-5 py-3 mx-3 rounded-full border whitespace-nowrap flex-shrink-0"
                style={{ borderColor: `${colors.charcoal}12` }}
              >
                <Icon className="h-5 w-5" style={{ color: colors.primary }} />
                <span className="text-sm font-medium" style={{ color: colors.charcoal }}>
                  {industry.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sensor Types Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-sm font-semibold uppercase tracking-wider mb-8 text-center"
          style={{ color: colors.midGray }}
        >
          Powered by sensors you choose
        </motion.p>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
        >
          {sensorTypes.map((sensor, index) => {
            const Icon = sensor.icon;
            return (
              <motion.div
                key={sensor.name}
                variants={fadeInUp}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="group p-5 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors duration-300"
                  style={{ backgroundColor: "hsl(var(--primary) / 0.03)" }}
                >
                  <Icon
                    className="h-5 w-5 transition-colors duration-300"
                    style={{ color: colors.primary }}
                  />
                </div>
                <h4
                  className="text-sm font-semibold mb-1"
                  style={{ color: colors.charcoal }}
                >
                  {sensor.name}
                </h4>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: colors.midGray }}
                >
                  {sensor.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* "And more" indicator */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-sm text-center mt-6"
          style={{ color: colors.midGray }}
        >
          ...and many more. We work with the sensors your operation needs.
        </motion.p>
      </div>

      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
      `}</style>
    </section>
  );
};

// ============================================================================
// PILOT PROGRAM CTA SECTION
// ============================================================================
interface PilotCTASectionProps {
  onApplyClick: () => void;
}

const PilotCTASection: React.FC<PilotCTASectionProps> = ({ onApplyClick }) => {
  return (
    <section className="pt-0 pb-40 md:pt-0 md:pb-48">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-2xl p-8 md:p-12 text-center overflow-hidden"
          style={{ backgroundColor: colors.champagne }}
        >
          <div>
            <span
              className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              Limited Spots Available
            </span>
            <h3
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ color: colors.charcoal }}
            >
              Join Our Pilot Program
            </h3>
            <p
              className="text-base md:text-lg mb-6 max-w-2xl mx-auto"
              style={{ color: colors.midGray }}
            >
              We're currently accepting applications from farms, breweries, and other operations
              looking to modernize their monitoring. Get early access and personalized onboarding.
            </p>
            <Button
              size="lg"
              className="text-base font-semibold px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              style={{ backgroundColor: colors.primary, color: colors.white }}
              onClick={onApplyClick}
            >
              Apply for Pilot Program
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// STOP WORRYING SECTION
// ============================================================================
const StopWorryingSection: React.FC = () => {
  const [currentWorry, setCurrentWorry] = React.useState(0);
  const [isInView, setIsInView] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  // Start cycling when section comes into view
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Cycle through worries
  React.useEffect(() => {
    if (!isInView) return;

    const interval = setInterval(() => {
      setCurrentWorry((prev) => (prev + 1) % worries.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <section ref={sectionRef} className="pt-20 pb-8 md:pt-28 md:pb-12 relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Intro */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-sm font-semibold uppercase tracking-wider mb-8"
          style={{ color: colors.midGray }}
        >
          Sound familiar?
        </motion.p>

        {/* Rotating worry */}
        <div className="h-16 md:h-20 flex items-center justify-center mb-12">
          <motion.p
            key={currentWorry}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl lg:text-4xl font-light italic"
            style={{ color: colors.charcoal }}
          >
            "{worries[currentWorry]}"
          </motion.p>
        </div>

        {/* Resolution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div
            className="w-16 h-px mx-auto mb-8"
            style={{ backgroundColor: colors.primary }}
          />
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ color: colors.charcoal }}>
            TerraMatrix handles it, so you don't have to.
          </h2>
          <p className="text-lg" style={{ color: colors.midGray }}>
            Get alerts when something needs attention. Otherwise, relax.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// INDUSTRIES SECTION
// ============================================================================
const IndustriesSection: React.FC = () => {
  return (
    <section id="industries" className="py-20 md:py-28" style={{ backgroundColor: colors.lightGray }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Solutions for Key Industries
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            TerraMatrix delivers value in any environment that runs on sensors and devices.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {industries.map((industry, index) => (
            <motion.div
              key={industry.title}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: industry.colorLight }}
              >
                <industry.icon className="h-8 w-8" style={{ color: industry.color }} />
              </div>
              <div className="mb-2">
                <span
                  className="text-sm font-medium uppercase tracking-wider"
                  style={{ color: industry.color }}
                >
                  {industry.subtitle}
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: colors.charcoal }}>
                {industry.title}
              </h3>
              <p className="leading-relaxed" style={{ color: colors.midGray }}>
                {industry.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// FEATURES SECTION - Bento Grid
// ============================================================================
const FeaturesSection: React.FC = () => {
  const Feature0Icon = features[0].icon;
  const Feature1Icon = features[1].icon;
  const Feature2Icon = features[2].icon;
  const Feature3Icon = features[3].icon;
  const Feature4Icon = features[4].icon;

  return (
    <section id="features" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Powerful Features
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            Built for teams who need real-time visibility and intelligent automation.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {/* Large card - spans 2 columns */}
          <motion.div
            variants={fadeInUp}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="md:col-span-2 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300"
            style={{ backgroundColor: colors.lightGray }}
          >
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "hsl(var(--primary) / 0.08)" }}
              >
                <Feature0Icon className="h-8 w-8" style={{ color: colors.primary }} />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
                  {features[0].title}
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: colors.midGray }}>
                  {features[0].description}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Regular card */}
          <motion.div
            variants={fadeInUp}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
            style={{ backgroundColor: colors.lightGray }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${colors.blueGrotto}15` }}
            >
              <Feature1Icon className="h-6 w-6" style={{ color: colors.blueGrotto }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: colors.charcoal }}>
              {features[1].title}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: colors.midGray }}>
              {features[1].description}
            </p>
          </motion.div>

          {/* Regular card */}
          <motion.div
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
            style={{ backgroundColor: colors.lightGray }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${colors.desertSun}15` }}
            >
              <Feature2Icon className="h-6 w-6" style={{ color: colors.desertSun }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: colors.charcoal }}>
              {features[2].title}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: colors.midGray }}>
              {features[2].description}
            </p>
          </motion.div>

          {/* Large card - spans 2 columns */}
          <motion.div
            variants={fadeInUp}
            transition={{ delay: 0.3 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="md:col-span-2 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300"
            style={{ backgroundColor: colors.lightGray }}
          >
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${colors.seafoam}30` }}
              >
                <Feature3Icon className="h-8 w-8" style={{ color: colors.primary }} />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
                  {features[3].title}
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: colors.midGray }}>
                  {features[3].description}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Full width card */}
          <motion.div
            variants={fadeInUp}
            transition={{ delay: 0.4 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="md:col-span-2 lg:col-span-3 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300"
            style={{ backgroundColor: colors.primary }}
          >
            <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white/20">
                <Feature4Icon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2 text-white">{features[4].title}</h3>
                <p className="text-lg leading-relaxed text-white/90">{features[4].description}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// BENEFITS SECTION - Smooth Infinite Marquee
// ============================================================================
const BenefitsSection: React.FC = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Duplicate benefits array for seamless loop
  const duplicatedBenefits = [...benefits, ...benefits];

  return (
    <section id="benefits" className="py-20 md:py-28 overflow-hidden" style={{ backgroundColor: colors.champagne }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Benefits for Your Team
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            Implementing TerraMatrix drives tangible improvements for operational teams.
          </motion.p>
        </motion.div>
      </div>

      {/* Infinite Marquee Container */}
      <div className="relative">
        {/* Fade edges */}
        <div
          className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to right, ${colors.champagne}, transparent)` }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to left, ${colors.champagne}, transparent)` }}
        />

        {/* CSS Keyframe animation for smooth infinite scroll */}
        <style>
          {`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-track {
              animation: marquee 35s linear infinite;
            }
            .marquee-track:hover {
              animation-play-state: paused;
            }
            @media (prefers-reduced-motion: reduce) {
              .marquee-track {
                animation: none;
              }
            }
          `}
        </style>

        {/* Scrolling container */}
        <div
          className={`flex gap-6 py-4 ${prefersReducedMotion ? '' : 'marquee-track'}`}
          style={{ width: "max-content" }}
        >
          {duplicatedBenefits.map((benefit, index) => (
            <div
              key={`${benefit.title}-${index}`}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300 flex-shrink-0"
              style={{ width: "280px" }}
            >
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                style={{ backgroundColor: "hsl(var(--primary) / 0.06)" }}
              >
                <benefit.icon className="h-6 w-6" style={{ color: colors.primary }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.charcoal }}>
                {benefit.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: colors.midGray }}>
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
interface CTASectionProps {
  onContactClick: () => void;
  onDemoClick: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({ onContactClick, onDemoClick }) => {
  return (
    <section className="pt-16 pb-20 md:pt-20 md:pb-28" style={{ backgroundColor: colors.primary }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to See It in Action?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-white/85 mb-8 max-w-xl mx-auto">
            Let's talk about what TerraMatrix can do for your operation. No pressure, just a conversation.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-base font-semibold min-w-[160px] px-6 py-5 rounded-xl border-2 shadow-lg hover:shadow-xl bg-background hover:bg-accent border-background text-primary transition-all duration-200"
              onClick={onContactClick}
            >
              Get in Touch
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="text-base font-semibold min-w-[160px] px-6 py-5 rounded-xl border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-200"
              onClick={onDemoClick}
            >
              Watch Demo
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN LANDING PAGE COMPONENT
// ============================================================================
export const LandingPageV1: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsiveInfo();

  // Scroll state
  const [scrolled, setScrolled] = useState(false);

  // Contact modal states
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle contact button click - modal on tablet/desktop, page on mobile
  const handleContactClick = () => {
    trackEvent("cta_click", {
      category: "engagement",
      label: "get_in_touch",
      location: isMobile ? "mobile_navigate" : "modal_open",
    });
    if (isMobile) {
      navigate("/contact");
    } else {
      setContactModalOpen(true);
    }
  };

  // Handle successful form submission
  const handleContactSuccess = (email: string) => {
    trackEvent("contact_form_submit", {
      category: "conversion",
      label: "pilot_program_application",
    });
    setSubmittedEmail(email);
    setContactModalOpen(false);
    setThankYouModalOpen(true);
  };

  // Handle thank you modal close
  const handleThankYouClose = () => {
    setThankYouModalOpen(false);
    setSubmittedEmail("");
  };

  // Handle demo button click
  const handleDemoClick = () => {
    trackEvent("demo_request", {
      category: "engagement",
      label: "watch_demo",
    });
    setDemoModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50/50 to-gray-50">
      <SEO
        title="TerraMatrix - IoT Sensor Analytics & Computer Vision for Industrial Monitoring"
        description="Transform your operations with TerraMatrix's IoT sensor systems and computer vision analytics. Real-time monitoring, predictive maintenance, and actionable insights for restaurants and industrial facilities."
        canonical="/"
        keywords="IoT sensor analytics, restaurant monitoring systems, industrial IoT, predictive maintenance, computer vision analytics, real-time monitoring, sensor systems, data aggregation"
        structuredData={organizationStructuredData}
      />
      <PublicNavigation scrolled={scrolled} onContactClick={handleContactClick} />
      <HeroSection onContactClick={handleContactClick} onDemoClick={handleDemoClick} />
      <PilotBanner onApplyClick={handleContactClick} />

      {/* Flowing content section */}
      <div className="relative">
        <WhySection />

        {/* Visual connector between sections */}
        <div className="flex justify-center -my-4 relative z-10">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
        </div>

        <HowItWorksSection />

        {/* Visual connector between sections */}
        <div className="flex justify-center -my-4 relative z-10">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
        </div>

        <FeaturedIndustriesSection />

        {/* Visual connector between sections */}
        <div className="flex justify-center -my-4 relative z-10">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
        </div>

        <StopWorryingSection />

        <PilotCTASection onApplyClick={handleContactClick} />
      </div>

      {/* Curved transition to CTA */}
      <div className="relative">
        <svg
          className="absolute top-0 left-0 w-full h-24 -translate-y-full text-primary"
          viewBox="0 0 1440 96"
          preserveAspectRatio="none"
          fill="none"
        >
          <path
            d="M0 96L60 85.3C120 75 240 53 360 48C480 43 600 53 720 58.7C840 64 960 64 1080 58.7C1200 53 1320 43 1380 37.3L1440 32V96H1380C1320 96 1200 96 1080 96C960 96 840 96 720 96C600 96 480 96 360 96C240 96 120 96 60 96H0Z"
            fill="currentColor"
          />
        </svg>
        <CTASection onContactClick={handleContactClick} onDemoClick={handleDemoClick} />
      </div>
      <LandingFooter />

      {/* Contact Modal - shown on tablet/desktop */}
      <PilotContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onSuccess={handleContactSuccess}
      />

      {/* Thank You Modal - shown after successful submission */}
      <ThankYouModal
        isOpen={thankYouModalOpen}
        onClose={handleThankYouClose}
        email={submittedEmail}
      />

      {/* Demo Modal - shows video placeholder */}
      <DemoModal
        isOpen={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
      />
    </div>
  );
};
